import apiClientService from "./ApiClientService";
import {
   downloadGeneratedDocument,
   normalizeDocumentDateParam,
} from "./examService";

const BASE_ENDPOINT = "/practical-exam-slots";

function readItems(raw, fallbackKeys = []) {
   if (Array.isArray(raw)) return raw;
   if (!raw || typeof raw !== "object") return [];

   const candidates = [
      "data",
      "items",
      "results",
      "rows",
      "slots",
      "reservations",
      ...fallbackKeys,
   ];

   for (const key of candidates) {
      const value = raw?.[key];
      if (Array.isArray(value)) return value;
      if (value && typeof value === "object") {
         if (Array.isArray(value.items)) return value.items;
         if (Array.isArray(value.data)) return value.data;
         if (Array.isArray(value.rows)) return value.rows;
         if (Array.isArray(value.slots)) return value.slots;
         if (Array.isArray(value.reservations)) return value.reservations;
      }
   }

   return [];
}

async function parseJsonSafe(res, fallback = null) {
   const contentType = (res.headers?.get?.("content-type") || "").toLowerCase();
   if (contentType.includes("application/json")) {
      try {
         return await res.json();
      } catch {
         return fallback;
      }
   }

   const text = await res.text().catch(() => "");
   if (!text) return fallback;

   try {
      return JSON.parse(text);
   } catch {
      return fallback ?? text;
   }
}

async function throwApiError(prefix, res) {
   const parsed = await parseJsonSafe(res, null);
   const message =
      typeof parsed === "string"
         ? parsed
         : parsed?.message || parsed?.error || parsed?.detail || res.statusText;

   throw new Error(`${prefix} ${res.status}: ${message || "eroare API"}`);
}

function encodeId(id, fnName) {
   const numericId = Number(id);
   if (!Number.isInteger(numericId) || numericId <= 0) {
      throw new Error(`${fnName}: id invalid.`);
   }

   return encodeURIComponent(String(numericId));
}

function cleanPayload(payload = {}) {
   const out = {};

   for (const [key, value] of Object.entries(payload || {})) {
      if (value === "" || value == null) continue;
      if (Array.isArray(value) && value.length === 0) continue;
      out[key] = value;
   }

   return out;
}

export async function getPracticalExamSlots() {
   const res = await apiClientService.get(BASE_ENDPOINT);
   if (!res.ok) {
      await throwApiError("getPracticalExamSlots", res);
   }

   const raw = await parseJsonSafe(res, []);
   return {
      raw,
      items: readItems(raw, ["practicalExamSlots"]),
   };
}

export async function getAvailablePracticalExamSlots() {
   const res = await apiClientService.get(`${BASE_ENDPOINT}/available`);
   if (!res.ok) {
      await throwApiError("getAvailablePracticalExamSlots", res);
   }

   const raw = await parseJsonSafe(res, []);
   return {
      raw,
      items: readItems(raw, ["availableSlots", "slots"]),
   };
}

export async function getMyPracticalExamReservation() {
   const res = await apiClientService.get(`${BASE_ENDPOINT}/my-reservation`);
   if (!res.ok) {
      await throwApiError("getMyPracticalExamReservation", res);
   }

   return await parseJsonSafe(res, null);
}

export async function getMyPracticalExamHistory() {
   const res = await apiClientService.get(`${BASE_ENDPOINT}/my-history`);
   if (!res.ok) {
      await throwApiError("getMyPracticalExamHistory", res);
   }

   const raw = await parseJsonSafe(res, []);
   return {
      raw,
      items: readItems(raw, ["history", "reservations"]),
   };
}

export async function getPracticalExamSlotById(id) {
   const slotId = encodeId(id, "getPracticalExamSlotById");
   const res = await apiClientService.get(`${BASE_ENDPOINT}/${slotId}`);
   if (!res.ok) {
      await throwApiError("getPracticalExamSlotById", res);
   }

   return await parseJsonSafe(res, null);
}

export async function createPracticalExamSlot(payload) {
   const res = await apiClientService.post(
      BASE_ENDPOINT,
      cleanPayload(payload),
      "application/json; charset=UTF-8",
   );
   if (!res.ok && res.status !== 201) {
      await throwApiError("createPracticalExamSlot", res);
   }

   return await parseJsonSafe(res, null);
}

export async function generatePracticalExamSlots(payload) {
   const res = await apiClientService.post(
      `${BASE_ENDPOINT}/generate`,
      cleanPayload(payload),
      "application/json; charset=UTF-8",
   );
   if (!res.ok && res.status !== 201) {
      await throwApiError("generatePracticalExamSlots", res);
   }

   return await parseJsonSafe(res, null);
}

export async function updatePracticalExamSlot(id, payload) {
   const slotId = encodeId(id, "updatePracticalExamSlot");
   const res = await apiClientService.patch(
      `${BASE_ENDPOINT}/${slotId}`,
      cleanPayload(payload),
      "application/json; charset=UTF-8",
   );
   if (!res.ok) {
      await throwApiError("updatePracticalExamSlot", res);
   }

   return await parseJsonSafe(res, null);
}

export async function deletePracticalExamSlot(id) {
   const slotId = encodeId(id, "deletePracticalExamSlot");
   const res = await apiClientService.delete(`${BASE_ENDPOINT}/${slotId}`);
   if (!res.ok && res.status !== 204) {
      await throwApiError("deletePracticalExamSlot", res);
   }

   return await parseJsonSafe(res, { id: Number(id) });
}

export async function getPracticalExamSlotReservations(id) {
   const slotId = encodeId(id, "getPracticalExamSlotReservations");
   const res = await apiClientService.get(`${BASE_ENDPOINT}/${slotId}/reservations`);
   if (!res.ok) {
      await throwApiError("getPracticalExamSlotReservations", res);
   }

   const raw = await parseJsonSafe(res, []);
   return {
      raw,
      items: readItems(raw, ["slotReservations", "reservations"]),
   };
}

export async function bookPracticalExamSlot(id, payload = {}) {
   const slotId = encodeId(id, "bookPracticalExamSlot");
   const body = cleanPayload(payload);
   const res = await apiClientService.post(
      `${BASE_ENDPOINT}/${slotId}/book`,
      body,
      "application/json; charset=UTF-8",
   );
   if (!res.ok && res.status !== 201) {
      await throwApiError("bookPracticalExamSlot", res);
   }

   return await parseJsonSafe(res, null);
}

export async function bookPracticalExamSlotForStudent(id, studentId, payload = {}) {
   const slotId = encodeId(id, "bookPracticalExamSlotForStudent");
   const source =
      studentId && typeof studentId === "object"
         ? studentId
         : { ...payload, studentId };
   const numericStudentId = Number(source.studentId);
   if (!Number.isInteger(numericStudentId) || numericStudentId <= 0) {
      throw new Error("bookPracticalExamSlotForStudent: studentId invalid.");
   }

   const res = await apiClientService.post(
      `${BASE_ENDPOINT}/${slotId}/book-for-student`,
      cleanPayload({ ...source, studentId: numericStudentId }),
      "application/json; charset=UTF-8",
   );
   if (!res.ok && res.status !== 201) {
      await throwApiError("bookPracticalExamSlotForStudent", res);
   }

   return await parseJsonSafe(res, null);
}

export async function setPracticalExamReservationStatus(reservationId, status) {
   const rid = encodeId(reservationId, "setPracticalExamReservationStatus");
   const res = await apiClientService.patch(
      `${BASE_ENDPOINT}/reservations/${rid}/status`,
      { status: String(status || "").trim().toUpperCase() },
      "application/json; charset=UTF-8",
   );
   if (!res.ok) {
      await throwApiError("setPracticalExamReservationStatus", res);
   }

   return await parseJsonSafe(res, null);
}

export async function updatePracticalExamReservation(reservationId, payload) {
   const rid = encodeId(reservationId, "updatePracticalExamReservation");
   const body = Object.fromEntries(
      Object.entries(payload || {}).filter(([, value]) => value != null),
   );
   const res = await apiClientService.patch(
      `${BASE_ENDPOINT}/reservations/${rid}`,
      body,
      "application/json; charset=UTF-8",
   );
   if (!res.ok) {
      await throwApiError("updatePracticalExamReservation", res);
   }

   return await parseJsonSafe(res, null);
}

export async function updatePracticalExamReservationColor(reservationId, color) {
   return updatePracticalExamReservation(reservationId, {
      color: String(color || "").trim(),
   });
}

export async function deletePracticalExamReservation(reservationId) {
   const rid = encodeId(reservationId, "deletePracticalExamReservation");
   const res = await apiClientService.delete(`${BASE_ENDPOINT}/reservations/${rid}`);
   if (!res.ok && res.status !== 204) {
      await throwApiError("deletePracticalExamReservation", res);
   }

   return await parseJsonSafe(res, { id: Number(reservationId) });
}

export async function cancelMyPracticalExamReservation() {
   const res = await apiClientService.patch(
      `${BASE_ENDPOINT}/my-reservation/cancel`,
      {},
      "application/json; charset=UTF-8",
   );
   if (!res.ok) {
      await throwApiError("cancelMyPracticalExamReservation", res);
   }

   return await parseJsonSafe(res, null);
}

export async function getAdminPracticalExamReservations(filters = {}) {
   const qs = new URLSearchParams();

   for (const [key, value] of Object.entries(filters || {})) {
      if (value === "" || value == null) continue;
      qs.set(key, String(value));
   }

   const suffix = qs.toString() ? `?${qs.toString()}` : "";
   const res = await apiClientService.get(
      `${BASE_ENDPOINT}/admin/reservations${suffix}`,
   );
   if (!res.ok) {
      await throwApiError("getAdminPracticalExamReservations", res);
   }

   const raw = await parseJsonSafe(res, []);
   return {
      raw,
      items: readItems(raw, ["adminReservations", "reservations"]),
   };
}

const practicalExamSlotsService = {
   getPracticalExamSlots,
   getAvailablePracticalExamSlots,
   getMyPracticalExamReservation,
   getMyPracticalExamHistory,
   getPracticalExamSlotById,
   createPracticalExamSlot,
   generatePracticalExamSlots,
   updatePracticalExamSlot,
   deletePracticalExamSlot,
   getPracticalExamSlotReservations,
   bookPracticalExamSlot,
   bookPracticalExamSlotForStudent,
   setPracticalExamReservationStatus,
   updatePracticalExamReservation,
   updatePracticalExamReservationColor,
   deletePracticalExamReservation,
   cancelMyPracticalExamReservation,
   getAdminPracticalExamReservations,
};

export default practicalExamSlotsService;

/* =========================
   Documente examen practic (Admin/Manager)
   Backend-ul le genereaza din datele rezervarii, nu din examenul teoretic.
========================= */
export const getPracticalExamIndividualDocumentUrl = (reservationId) =>
   `${BASE_ENDPOINT}/documents/individual/${encodeURIComponent(
      String(reservationId),
   )}`;

export function getPracticalExamDailyProcessVerbalUrl(date) {
   const day = normalizeDocumentDateParam(date);
   const base = `${BASE_ENDPOINT}/documents/daily-process-verbal`;
   return day ? `${base}?date=${encodeURIComponent(day)}` : base;
}

/** Procesul-verbal individual al unei rezervari de examen practic. */
export async function downloadPracticalExamIndividualDocument(
   reservationId,
   filename,
) {
   const rid = Number(reservationId);
   if (!Number.isInteger(rid) || rid <= 0) {
      throw new Error("Lipsește id-ul rezervării de examen.");
   }
   return downloadGeneratedDocument(getPracticalExamIndividualDocumentUrl(rid), {
      fallbackName: filename || `proces-verbal-examen-practic-${rid}.pdf`,
   });
}

/** Procesul-verbal al zilei pentru examenul practic: toate rezervarile zilei (implicit ziua curenta). */
export async function downloadPracticalExamDailyProcessVerbal(date, filename) {
   const day = normalizeDocumentDateParam(date);
   return downloadGeneratedDocument(getPracticalExamDailyProcessVerbalUrl(day), {
      fallbackName: filename || `borderou-examen-practic-${day || "azi"}.pdf`,
   });
}
